/**
 * @fileoverview HTTP layer of the student Tasks Tracker (AR-898 / AR-897).
 *
 * `GET /api/arc_mosaico/task_track/student?project_id=` — student profile only.
 *   401 → the API helper resolves it; we send the user to `/error_permission`
 *         like the teacher's service does.
 *   422 → "Project not found" (not enrolled / wrong school / not trackable) or
 *         "Student not found": surfaced as `StudentTrackerNotEnrolledError` so
 *         the UI shows a friendly empty state instead of an error toast.
 */

import { ARC_MOSAICO_ENDPOINTS } from 'api/arcMosaico'
import { api } from 'helpers'
import type { StudentTrackerMatrix } from '../types'
import { adaptStudentMatrix, type RawStudentMatrix } from './adapters'

const HTTP_UNAUTHORIZED = 401
const HTTP_UNPROCESSABLE = 422
const PERMISSION_ERROR_PATH = '/error_permission'

export class StudentTrackerNotEnrolledError extends Error {
  constructor(message = 'Project not found') {
    super(message)
    this.name = 'StudentTrackerNotEnrolledError'
  }
}

interface HttpErrorLike {
  response?: { status?: number; data?: { errors?: unknown; error?: unknown } }
}

function isUnprocessable(error: unknown): error is HttpErrorLike {
  return typeof error === 'object' && error !== null && (error as HttpErrorLike).response?.status === HTTP_UNPROCESSABLE
}

export async function fetchStudentTrackMatrix(projectId: string): Promise<StudentTrackerMatrix> {
  let response
  try {
    response = await api.get(ARC_MOSAICO_ENDPOINTS.taskTrack.studentMatrix, { params: { projectId } })
  } catch (error) {
    if (isUnprocessable(error)) {
      const detail = error.response?.data?.errors ?? error.response?.data?.error
      throw new StudentTrackerNotEnrolledError(typeof detail === 'string' ? detail : undefined)
    }
    throw error
  }
  if (response.status === HTTP_UNAUTHORIZED) {
    window.location.href = PERMISSION_ERROR_PATH
    throw new Error('Unauthorized')
  }
  return adaptStudentMatrix(response.data as RawStudentMatrix)
}
